import React, {useState, useEffect} from 'react';
import {
  View,
  Text,
  TextInput,
  ActivityIndicator,
  Image,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Keyboard,
  Alert,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {styles} from './styles';
import Header from '../../components/Header/Header';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import TemporaryPasswordBanner from '../../components/TemporaryPasswordBanner/TemporaryPasswordBanner';
import CardsService from '../../services/CardsService/CardsService';
import IoPayService from '../../services/IoPayService/IoPayService';
import {useUserStore} from '../../store/UserStore';
import {Images} from '../../constants/Images';
import {extractErrorMessage} from '../../utils/extractErrorMessage';
import { showSuccess, showInfo } from '../../utils/globalAlert';

const detectCardBrand = (number: string) => {
  if (/^4/.test(number)) return 'Visa';
  if (/^5[1-5]/.test(number)) return 'MasterCard';
  if (/^3[47]/.test(number)) return 'American Express';
  return '';
};

const estadosValidos = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const NewCard = () => {
  const {goBack} = useNavigation();
  const {user} = useUserStore();
  const temporaryPasswordLogin = useUserStore(state => state.temporaryPasswordLogin);
  const [cardNumber, setCardNumber] = useState('');
  const [cardholder, setCardholder] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [cep, setCep] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [brand, setBrand] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [existingCustomer, setExistingCustomer] = useState(null);
  const [customerFieldErrors, setCustomerFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const checkCustomer = async () => {
      setLoading(true);
      try {
        const customer = await CardsService.GetCustomer(user?.clientCode);

        if (!!customer?.customer) {
          setExistingCustomer(customer?.customer);
        } else {
          setExistingCustomer(null);
        }
      } catch (error) {
        console.error('Erro ao verificar cliente:', error);
        setExistingCustomer(null);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    checkCustomer();
  }, [user?.clientCode]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleCardNumberChange = (text: string) => {
    const cleaned = text.replace(/\D/g, '').slice(0, 16);
    const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();
    setCardNumber(formatted);

    const detected = detectCardBrand(cleaned);
    if (detected) {
      setBrand(detected);
    }
  };

  const handleStateChange = (text: string) => {
    const cleanedText = text
      .replace(/[^a-zA-Z]/g, '')
      .toUpperCase()
      .slice(0, 2);

    if (estadosValidos.includes(cleanedText)) {
      setState(cleanedText);
    } else {
      setState(cleanedText);
    }
  };

  const handleCepChange = (text: string) => {
    const cleaned = text.replace(/\D/g, '').slice(0, 8);
    let formatted = cleaned;
    if (cleaned.length >= 6) {
      formatted = cleaned.slice(0, 5) + '-' + cleaned.slice(5);
    }
    setCep(formatted);

    // Se o CEP tem 8 dígitos, consulta a API
    if (cleaned.length === 8) {
      consultarCep(cleaned);
    }
  };

  const consultarCep = async (cepNumeros: string) => {
    setLoadingCep(true);
    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cepNumeros}/json/`,
      );
      const data = await response.json();

      if (data.erro) {
        Alert.alert('CEP não encontrado', 'Verifique o CEP informado');
        return;
      }

      // Preenche automaticamente os campos
      setAddress(data.logradouro || '');
      setCity(data.localidade || '');
      setState(data.uf || '');

      // Se não encontrou o logradouro, foca no campo de endereço para o usuário completar
      if (!data.logradouro) {
        showInfo(
          'Endereço incompleto',
          'CEP encontrado, mas é necessário completar o endereço manualmente',
        );
      }
    } catch (error) {
      console.error('Erro ao consultar CEP:', error);
      Alert.alert(
        'Erro ao consultar CEP',
        'Não foi possível consultar o CEP. Verifique sua conexão com a internet.',
      );
    } finally {
      setLoadingCep(false);
    }
  };

  const handleExpiryChange = (text: string) => {
    const cleaned = text.replace(/\D/g, '').slice(0, 4);
    let formatted = cleaned;
    if (cleaned.length >= 3) {
      formatted = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    }
    setExpiry(formatted);
  };

  const isExpiryValid = () => {
    if (!/^\d{2}\/\d{2}$/.test(expiry)) return false;

    const [monthStr, yearStr] = expiry.split('/');
    const month = parseInt(monthStr, 10);
    const year = parseInt(`20${yearStr}`, 10);

    if (month < 1 || month > 12) return false;

    const now = new Date();
    const expiryDate = new Date(year, month);
    const currentMonth = new Date(now.getFullYear(), now.getMonth() + 1);

    return expiryDate >= currentMonth;
  };

  const validateCard = () => {
    const cleaned = cardNumber.replace(/\D/g, '');
    const baseValidation =
      (cleaned.length === 15 || cleaned.length === 16) &&
      cardholder.trim().length > 0 &&
      cvv.trim().length >= 3 &&
      isExpiryValid();

    // Se já tem customer existente, não precisa validar endereço
    if (existingCustomer) {
      return baseValidation;
    }

    // Se não tem customer, precisa validar os campos de endereço, nome e email
    const addressValidation =
      customerName.trim().length > 0 &&
      customerEmail.trim().length > 0 &&
      validateEmail(customerEmail) &&
      cep.replace(/\D/g, '').length === 8 &&
      address.trim().length > 0 &&
      city.trim().length > 0 &&
      estadosValidos.includes(state);

    return baseValidation && addressValidation;
  };

  const authenticate = async () => {
    const response = await IoPayService.IoPayAuth();
    return response.access_token;
  };

  const tokenizeCard = async (token: any) => {
    const response = await IoPayService.TokenizeCard(token, {
      holder_name: cardholder,
      expiration_month: parseInt(expiry.slice(0, 2), 10),
      expiration_year: parseInt(expiry.slice(3), 10),
      card_number: cardNumber.replace(/\D/g, ''),
      security_code: cvv,
    });

    if (response?.error) {
      const message = extractErrorMessage({data: response});
      throw new Error(message);
    }

    return response;
  };

  const formatCPF = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatCNPJ = (cnpj: string) => {
    return cnpj.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      '$1.$2.$3/$4-$5',
    );
  };

  const formatTaxpayerId = (taxpayerId: string) => {
    if (taxpayerId.length === 11) {
      return formatCPF(taxpayerId);
    } else if (taxpayerId.length === 14) {
      return formatCNPJ(taxpayerId);
    }
    return taxpayerId;
  };

  const createCustomerIfNeeded = async () => {
    if (!existingCustomer) {
      if (!customerName.trim()) {
        setCustomerFieldErrors(prev => ({...prev, name: 'Por favor, preencha o nome.'}));
        Alert.alert('Erro', 'Por favor, preencha o nome.');
        throw new Error('Nome não preenchido');
      }


      if (!customerEmail.trim()) {
        setCustomerFieldErrors(prev => ({...prev, email: 'Por favor, preencha o email.'}));
        Alert.alert('Erro', 'Por favor, preencha o email.');
        throw new Error('Email não preenchido');
      }

      if (!validateEmail(customerEmail)) {
        setCustomerFieldErrors(prev => ({...prev, email: 'Por favor, insira um email válido.'}));
        Alert.alert('Erro', 'Por favor, insira um email válido.');
        throw new Error('Email inválido');
      }

      const client = user?.current_client;
      if (client) {
        const data = {
          client_code: user?.clientCode,
          first_name: customerName.trim(),
          email: customerEmail.trim().toLowerCase(),
          taxpayer_id: formatTaxpayerId(client.cnpj_cpf),
          address: {
            city: city,
            state: state,
            line1: address,
          },
        };
        const response = await CardsService.CreateCustomer(data);
        return response;
      }
    }
    return null;
  };

  const handleSaveCard = async () => {
    setLoading(true);
    try {
      // 1. Garantir que o cliente existe
      if (!existingCustomer) {
        await createCustomerIfNeeded();
      }

      // 2. Autenticar
      const token = await authenticate();
      // 3. Tokenizar o cartão
      const tokenizedCard = await tokenizeCard(token);
      // 4. Autenticar de novo
      const token2 = await authenticate();
      // 5. Associar cartão
      await CardsService.RegisterCard(
        user?.clientCode,
        tokenizedCard.id,
        token2,
      );

      showSuccess('Sucesso!', 'Cartão registrado com sucesso!');
      goBack();
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      Alert.alert('Erro ao salvar o cartão!', errorMessage);
      console.error('Erro ao salvar o cartão:', error);
    } finally {
      setLoading(false);
    }
  };

  const cardBrandImage = () => {
    if (brand === 'Visa') return Images.visaIcon;
    if (brand === 'MasterCard') return Images.masterCardIcon;
    return '';
  };

  const needsAddressInfo = !existingCustomer;

  return (
    <KeyboardAvoidingView
      style={{flex: 1}}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      enabled>
      <ScrollView
          contentContainerStyle={[styles.container, {flexGrow: 1, paddingBottom: 40}]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <Header />
          <View style={styles.spaceHeader}>
            <SearchHeader onPress={goBack} title="Novo Cartão" mb={20} />
          </View>
          <TemporaryPasswordBanner />

          {loading && (
            <View style={{padding: 16}}>
              <ActivityIndicator size="large" color="#000" />
            </View>
          )}

          <View style={styles.cardPreview}>
            <View style={styles.brandContainer}>
              {brand ? (
                <Image source={cardBrandImage()} style={styles.brandLogo} />
              ) : (
                <Text style={styles.brandPlaceholder}>Bandeira</Text>
              )}
            </View>
            <Text style={styles.cardNumberText}>
              {cardNumber || '**** **** **** ****'}
            </Text>
            <Text style={styles.cardholderText}>
              {cardholder || 'NOME DO TITULAR'}
            </Text>
            <Text style={styles.expiryText}>
              {expiry || 'MM/AA'} {cvv ? `• ${cvv}` : ''}
            </Text>
          </View>

          <TextInput
            style={styles.input}
            placeholder="Número do cartão"
            placeholderTextColor="#999"
            keyboardType="numeric"
            maxLength={19}
            value={cardNumber}
            onChangeText={handleCardNumberChange}
          />
          <TextInput
            style={styles.input}
            placeholder="Nome do titular"
            placeholderTextColor="#999"
            value={cardholder}
            onChangeText={text => setCardholder(text)}
          />
          <TextInput
            style={styles.input}
            placeholder="Data de validade (MM/AA)"
            placeholderTextColor="#999"
            keyboardType="numeric"
            maxLength={5}
            value={expiry}
            onChangeText={handleExpiryChange}
          />
          <TextInput
            style={styles.input}
            placeholder="Código de segurança"
            placeholderTextColor="#999"
            maxLength={4}
            keyboardType="numeric"
            value={cvv}
            onChangeText={text => setCvv(text)}
          />

          {/* Campos de endereço - só aparecem se não houver customer existente */}
          {needsAddressInfo && (
            <>
              <Text
                style={[
                  styles.sectionTitle,
                  {marginTop: 20, marginBottom: 10},
                ]}>
                Informações Pessoais
              </Text>

              {/* Campo de Nome */}
              <TextInput
                style={[styles.input, customerFieldErrors.name && {borderColor: 'red'}]}
                placeholder="Nome completo"
                placeholderTextColor="#999"
                value={customerName}
                onChangeText={text => {
                  setCustomerName(text);
                  if (customerFieldErrors.name) {
                    setCustomerFieldErrors(prev => ({...prev, name: undefined}));
                  }
                }}
              />
              {customerFieldErrors.name && (
                <Text style={{color: 'red', marginTop: -8, marginBottom: 8, fontSize: 12}}>
                  {customerFieldErrors.name}
                </Text>
              )}

              {/* Campo de Email */}
              <TextInput
                style={[styles.input, customerFieldErrors.email && {borderColor: 'red'}]}
                placeholder="Email"
                placeholderTextColor="#999"
                keyboardType="email-address"
                autoCapitalize="none"
                value={customerEmail}
                onChangeText={text => {
                  setCustomerEmail(text);
                  if (customerFieldErrors.email) {
                    setCustomerFieldErrors(prev => ({...prev, email: undefined}));
                  }
                }}
              />
              {customerEmail && !validateEmail(customerEmail) && (
                <Text style={{color: 'red', marginTop: -8, marginBottom: 8, fontSize: 12}}>
                  Email inválido
                </Text>
              )}
              {customerFieldErrors.email &&
                !(customerEmail && !validateEmail(customerEmail)) && (
                  <Text style={{color: 'red', marginTop: -8, marginBottom: 8, fontSize: 12}}>
                    {customerFieldErrors.email}
                  </Text>
                )}

              <Text
                style={[
                  styles.sectionTitle,
                  {marginTop: 20, marginBottom: 10},
                ]}>
                Informações de Endereço
              </Text>

              <View style={{position: 'relative'}}>
                <TextInput
                  style={styles.input}
                  placeholder="CEP"
                  placeholderTextColor="#999"
                  keyboardType="numeric"
                  maxLength={9}
                  value={cep}
                  onChangeText={handleCepChange}
                />
                {loadingCep && (
                  <View style={{position: 'absolute', right: 12, top: 12}}>
                    <ActivityIndicator size="small" color="#666" />
                  </View>
                )}
              </View>

              <TextInput
                style={styles.input}
                placeholder="Endereço de cobrança"
                placeholderTextColor="#999"
                value={address}
                onChangeText={text => setAddress(text)}
              />
              <TextInput
                style={styles.input}
                placeholder="Cidade"
                placeholderTextColor="#999"
                value={city}
                onChangeText={text => setCity(text)}
              />
              <TextInput
                style={styles.input}
                placeholder="Estado"
                placeholderTextColor="#999"
                value={state}
                onChangeText={text => handleStateChange(text)}
                maxLength={2}
              />
              {state &&
                state.length == 2 &&
                !estadosValidos.includes(state) && (
                  <Text style={{color: 'red', marginTop: -8, marginBottom: 8, fontSize: 12}}>
                    UF inválido
                  </Text>
                )}
            </>
          )}

          <ButtonComponent
            title="Continuar"
            disabled={
              !validateCard() ||
              loading ||
              loadingCep ||
              (needsAddressInfo &&
                state.length === 2 &&
                !estadosValidos.includes(state)) ||
              temporaryPasswordLogin
            }
            onPress={handleSaveCard}
            width="100%"
          />
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

export default NewCard;